"use client";

import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Download, Upload } from "lucide-react";

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export default function ImageCompressorClient() {
  const [originalSize, setOriginalSize] = useState<number | null>(null);
  const [originalPreview, setOriginalPreview] = useState<string | null>(null);
  const [compressedUrl, setCompressedUrl] = useState<string | null>(null);
  const [compressedSize, setCompressedSize] = useState<number | null>(null);
  const [quality, setQuality] = useState(0.7);
  const [fileName, setFileName] = useState("image");
  const [processing, setProcessing] = useState(false);
  const imgRef = useRef<HTMLImageElement | null>(null);

  const handleFile = (file: File) => {
    setOriginalSize(file.size);
    setFileName(file.name.replace(/\.[^.]+$/, ""));
    const reader = new FileReader();
    reader.onload = () => {
      setOriginalPreview(reader.result as string);
      setCompressedUrl(null);
      setCompressedSize(null);
    };
    reader.readAsDataURL(file);
  };

  const compress = () => {
    if (!originalPreview) return;
    setProcessing(true);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        setProcessing(false);
        return;
      }
      ctx.drawImage(img, 0, 0);
      canvas.toBlob(
        (blob) => {
          if (blob) {
            setCompressedUrl(URL.createObjectURL(blob));
            setCompressedSize(blob.size);
          }
          setProcessing(false);
        },
        "image/jpeg",
        quality
      );
    };
    img.src = originalPreview;
  };

  return (
    <div className="space-y-6 pb-10">
      <div className="border-2 border-dashed rounded-2xl p-8 text-center space-y-3">
        <Upload className="mx-auto text-muted-foreground" size={28} />
        <p className="text-sm text-muted-foreground">Choose an image to compress (JPEG, PNG, WebP)</p>
        <input
          type="file"
          accept="image/*"
          onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
          className="text-sm mx-auto file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-primary file:text-primary-foreground file:text-sm file:font-medium hover:file:opacity-90 file:cursor-pointer"
        />
      </div>

      {originalPreview && (
        <>
          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">
              Quality: {Math.round(quality * 100)}%
            </label>
            <input
              type="range"
              min={0.1}
              max={1}
              step={0.05}
              value={quality}
              onChange={(e) => setQuality(parseFloat(e.target.value))}
              className="w-full"
            />
          </div>

          <Button onClick={compress} disabled={processing}>
            {processing ? "Compressing..." : "Compress Image"}
          </Button>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">
                Original {originalSize !== null && `(${formatBytes(originalSize)})`}
              </p>
              <img src={originalPreview} alt="Original" className="w-full rounded-xl border" />
            </div>
            {compressedUrl && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">
                  Compressed {compressedSize !== null && `(${formatBytes(compressedSize)})`}
                  {originalSize && compressedSize && (
                    <span className="text-primary ml-2">
                      -{Math.round((1 - compressedSize / originalSize) * 100)}%
                    </span>
                  )}
                </p>
                <img src={compressedUrl} alt="Compressed" className="w-full rounded-xl border" />
                <a href={compressedUrl} download={`${fileName}-compressed.jpg`}>
                  <Button variant="outline" size="sm" className="gap-2 mt-2">
                    <Download size={14} /> Download
                  </Button>
                </a>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
